import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Copy, Check, Eye, EyeOff, RefreshCw, KeyRound, IdCard, Mail, Phone } from "lucide-react";

/**
 * Admin utility: create fresh demo IBAs in one click, no DevTools required.
 * Uses existing POST /admin/demo-iba/create + GET /admin/demo-iba/list endpoints.
 * Shows plaintext credentials on-screen exactly once — persisted via demo_ibas collection.
 */
export default function DemoIbaCard() {
  const [busy, setBusy] = useState(false);
  const [latest, setLatest] = useState(null); // the fresh one from THIS click
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const loadHistory = () =>
    api.get("/admin/demo-iba/list").then((r) => setHistory(r.data.items || [])).catch(() => {});

  useEffect(() => { loadHistory(); }, []);

  const mint = async () => {
    setBusy(true);
    setLatest(null);
    try {
      const { data } = await api.post("/admin/demo-iba/create");
      setLatest(data);
      toast.success(`Demo IBA ${data.user_code} minted`);
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to mint IBA");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="mint-demo-iba-card"
      className="bg-white border border-border rounded-sm overflow-hidden"
    >
      <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 grid place-items-center bg-royal/10 border border-royal/20 rounded-sm">
            <UserPlus size={15} className="text-royal" />
          </div>
          <div>
            <h3 className="font-serif text-lg text-navy">Mint Test IBA</h3>
            <p className="text-[11px] text-navy/50 -mt-0.5">One-click distributor for testing the live tree</p>
          </div>
        </div>
        <button
          type="button"
          onClick={mint}
          disabled={busy}
          data-testid="mint-demo-iba-btn"
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-4 py-2 bg-royal text-white hover:bg-royal-deep rounded-sm disabled:opacity-50"
        >
          <RefreshCw size={12} className={busy ? "animate-spin" : ""} />
          {busy ? "Creating…" : "Create Test IBA"}
        </button>
      </div>

      {/* Fresh credentials — shown once, prominently */}
      {latest ? (
        <div className="p-5 bg-gradient-to-br from-olive/5 via-white to-gold/5 border-b border-border" data-testid="mint-demo-iba-result">
          <div className="flex items-center gap-2 mb-3">
            <Check size={14} className="text-olive" />
            <span className="text-[11px] uppercase tracking-widest text-olive font-medium">Ready to test — copy now</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <CredRow icon={IdCard} label="IBA ID" value={latest.user_code} tid="mint-cred-code" />
            <CredRow icon={Mail} label="Email" value={latest.email} tid="mint-cred-email" />
            <CredRow icon={KeyRound} label="Password" value={latest.password_plain} tid="mint-cred-pw" secret />
            <CredRow icon={Phone} label="Sponsor · Side" value={`${latest.sponsor || "—"} · ${latest.placement_side || "—"}`} tid="mint-cred-sponsor" />
          </div>
          <div className="mt-3 flex items-center justify-between text-[11px] text-navy/50">
            <span>These credentials are shown once. Also stored in <code>demo_ibas</code> collection.</span>
            <CopyAllButton latest={latest} />
          </div>
        </div>
      ) : (
        <div className="px-5 py-4 text-[12.5px] text-navy/60 border-b border-border">
          Click <b>Create Test IBA</b> to mint a live distributor account on this environment. Credentials appear here instantly — no DevTools needed.
        </div>
      )}

      {/* Previously minted list */}
      <div className="px-5 py-3 flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest text-navy/50">
          Previously minted ({history.length})
        </div>
        <button
          type="button"
          onClick={() => setShowHistory((v) => !v)}
          data-testid="mint-demo-iba-toggle-history"
          className="text-[11px] text-royal hover:underline inline-flex items-center gap-1"
        >
          {showHistory ? <><EyeOff size={11} /> Hide</> : <><Eye size={11} /> Show</>}
        </button>
      </div>
      {showHistory && (
        <div className="max-h-64 overflow-y-auto border-t border-border">
          {history.length === 0 ? (
            <div className="p-4 text-xs text-navy/50 italic">No demo IBAs minted yet.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-cream/40 text-navy/50">
                <tr>
                  <th className="text-left px-4 py-2 font-medium uppercase tracking-wider text-[10px]">IBA ID</th>
                  <th className="text-left px-4 py-2 font-medium uppercase tracking-wider text-[10px]">Email</th>
                  <th className="text-left px-4 py-2 font-medium uppercase tracking-wider text-[10px]">Password</th>
                  <th className="text-left px-4 py-2 font-medium uppercase tracking-wider text-[10px]">Created</th>
                </tr>
              </thead>
              <tbody className="text-navy">
                {history.map((h) => (
                  <tr key={h.user_code} className="border-t border-border/60 hover:bg-cream/20" data-testid={`mint-demo-history-${h.user_code}`}>
                    <td className="px-4 py-2 font-mono">{h.user_code}</td>
                    <td className="px-4 py-2 text-navy/70">{h.email}</td>
                    <td className="px-4 py-2 font-mono">
                      <SecretCell value={h.password_plain} />
                    </td>
                    <td className="px-4 py-2 text-navy/50">{fmtDate(h.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </section>
  );
}

function CredRow({ icon: Icon, label, value, tid, secret }) {
  const copy = () => {
    navigator.clipboard.writeText(value || "");
    toast.success(`${label} copied`);
  };
  return (
    <div className="flex items-center gap-2 bg-white border border-border rounded-sm px-3 py-2" data-testid={tid}>
      <Icon size={13} className="text-gold-deep shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-widest text-navy/50">{label}</div>
        {secret ? (
          <SecretCell value={value} className="text-sm text-navy" />
        ) : (
          <div className="text-sm text-navy truncate font-mono">{value}</div>
        )}
      </div>
      <button
        type="button"
        onClick={copy}
        title={`Copy ${label}`}
        data-testid={`${tid}-copy`}
        className="p-1.5 text-navy/50 hover:text-royal hover:bg-royal/5 rounded"
      >
        <Copy size={12} />
      </button>
    </div>
  );
}

function SecretCell({ value, className = "" }) {
  const [reveal, setReveal] = useState(false);
  return (
    <div className={`inline-flex items-center gap-2 ${className}`}>
      <span className="font-mono">{reveal ? value : "•".repeat(Math.min((value || "").length, 12))}</span>
      <button type="button" onClick={() => setReveal((v) => !v)} className="text-navy/50 hover:text-royal" title={reveal ? "Hide" : "Reveal"}>
        {reveal ? <EyeOff size={11} /> : <Eye size={11} />}
      </button>
      <button type="button" onClick={() => { navigator.clipboard.writeText(value || ""); toast.success("Copied"); }} className="text-navy/50 hover:text-royal" title="Copy">
        <Copy size={11} />
      </button>
    </div>
  );
}

function CopyAllButton({ latest }) {
  const [copied, setCopied] = useState(false);
  const doCopy = () => {
    const text = `IBA ID: ${latest.user_code}\nEmail:  ${latest.email}\nPassword: ${latest.password_plain}\nSponsor: ${latest.sponsor}\nPlacement: ${latest.placement_side}`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("All credentials copied");
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      type="button"
      onClick={doCopy}
      data-testid="mint-demo-iba-copyall"
      className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 border border-royal/20 text-royal hover:bg-royal/5 rounded-sm"
    >
      {copied ? <Check size={11} /> : <Copy size={11} />} Copy all
    </button>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
